export interface DeckDoc {
  title: string
  content: string
  // Optional grouping for the DocViewer tree sidebar. Docs sharing a group
  // (in declaration order) are nested under one collapsible heading.
  group?: string
}

export type DeckTheme = 'light' | 'dark'

// Public surface of the deck engine (src/decks/shared/engine.js) that canvas
// modules and initDeck are allowed to use.
export interface DeckEngine {
  onSlideChange: (cb: (slide: number) => void) => void
  offSlideChange: (cb: (slide: number) => void) => void
  getCurrentSlide: () => number
  goTo: (n: number) => void
  destroy: () => void
  readonly total: number
}

export interface DeckModule {
  html: string
  title: string
  subtitle: string
  slideCount: number
  date: string
  ogDescription?: string
  defaultTheme?: DeckTheme
  docs?: DeckDoc[]
  initDeck: (rootEl: HTMLElement, engine: DeckEngine) => void
}

// Static metadata for OG tags (available without loading the deck module)
export interface DeckMeta {
  slug: string
  title: string
  subtitle: string
  ogDescription: string
  // ISO date, mirrors each deck's init.ts `date` export. Used to order the
  // home listing (oldest → newest) without loading every deck module.
  date: string
  defaultTheme?: DeckTheme
}

export const deckMetadata: DeckMeta[] = [
  {
    slug: 'recsys-agent',
    title: 'Personalized Feed Agents',
    subtitle: 'Agent-driven feed curation for GlobalComix',
    ogDescription: 'An agent pipeline that learns the catalog deeply, generates editorial content, and builds daily personalized feeds for every reader archetype.',
    date: '2026-03-26',
  },
  {
    slug: 'bd-agent',
    title: 'The Invisible Engine',
    subtitle: 'AI-powered business development agents',
    ogDescription: 'AI-powered business development for global IP licensing. Six agents, eleven MCP servers, one human.',
    date: '2026-03-15',
  },
  {
    slug: 'iris',
    title: 'IRIS',
    subtitle: 'The browser our agents drive',
    ogDescription: 'A Chrome-based browser agent that reads the sites ChatGPT, Claude, and Playwright can\'t — signed in, like a human user.',
    date: '2026-04-20',
  },
  {
    slug: 'data-warehouse',
    title: 'Data Warehouse Overview',
    subtitle: 'Where product data lives and how it flows',
    ogDescription: 'A non-technical overview of the GlobalComix data warehouse: four datasets, nine key tables, and the pipelines that keep product data clean and reusable.',
    date: '2026-04-01',
  },
  {
    slug: 'discovery-strategy',
    title: 'GlobalComix Discovery Strategy',
    subtitle: 'Widen the doorway; fix the leaks',
    ogDescription: 'How GlobalComix grows its #1 acquisition channel: fix indexation and Core Web Vitals, convert more search traffic, and turn social into reads.',
    date: '2026-06-20',
    defaultTheme: 'dark',
  },
  {
    slug: 'creator-growth',
    title: 'GlobalComix Creator Growth Strategy',
    subtitle: 'Arm the creators; they bring the readers',
    ogDescription: 'Turn GlobalComix creators into the distribution engine: opt-in AI marketing tools, self-localization, multi-publish, and an ambassador loop — feeding low-CAC acquisition (OKR 2) and the English-manga destination incl. self-serve JP manga (OKR 3).',
    date: '2026-06-22',
    defaultTheme: 'dark',
  },
  {
    slug: 'test-deck',
    title: 'Comment Feature Test',
    subtitle: 'A minimal deck for testing',
    ogDescription: 'A minimal test deck for verifying comments and presence features.',
    date: '2026-03-26',
  },
  {
    slug: 'gem-editor',
    title: 'Gem Editor Revamp',
    subtitle: 'A real timeline, configurable workflows, next-gen video models',
    ogDescription: 'Proposal to revamp the Gem Editor chapter page: resizable timeline, React Flow workflows per item, and full Seedance 2 input support.',
    date: '2026-04-23',
  },
  {
    slug: 'visitor-feed',
    title: 'The Future of Our Feed',
    subtitle: 'AI-powered feed curation for GlobalComix',
    ogDescription: 'How an AI agent pipeline replaces static CMS feeds with editorially sharp, audience-specific curation — reviewed by humans, personalized by RecSys.',
    date: '2026-04-08',
  },
  {
    slug: 'metadata-graph',
    title: 'An Anchored Knowledge Graph',
    subtitle: 'Closed-world backbone, open-world enrichment',
    ogDescription: 'Design Memo 04: a per-comic metadata graph — a closed-world backbone of business anchors plus an open-world enrichment graph that always traces back to an anchor via evidence. Add entity types forever without rewriting the spine.',
    date: '2026-05-26',
  },
  {
    slug: 'gold-retention',
    title: 'Gold Retention Program',
    subtitle: 'Fixing the first-month cliff for monthly Gold subscribers',
    ogDescription: 'Half of new Gold subscribers quit after their first payment — the same ~19K that is the entire distance to the Q3 KR. The fix: win week two by escalating until they read, across five reusable app surfaces. The app-side companion to our web conversion work.',
    date: '2026-07-13',
  },
  {
    slug: 'app-pricing-experiments',
    title: 'Take the Pricing Wins to the App',
    subtitle: 'The web found the offer; the law opened the door; the app is where the customers are',
    ogDescription: 'The web $1-first-month offer is producing 3x the paying members of control — and 89% of our subscribers are born on the app, where a court ruling now lets us run any Stripe pricing experiment via signed-in web checkout. Staged rollout: the first two segments cannot lose money.',
    date: '2026-08-10',
  },
  {
    slug: 'metadata-base',
    title: 'Project Nico',
    subtitle: 'Shared comic data for our teams. Localization and trailers first.',
    ogDescription: 'Keep track of the people, places, objects and stories in our comics, with links to the source pages. Support localization and trailers first, with new-app support still in discussion.',
    date: '2026-09-08',
  },
  {
    slug: 'gold-retention-findings',
    title: 'Gold Retention — Findings & What’s Next',
    subtitle: 'The first fix worked; the analysis says what to buy next',
    ogDescription: 'We made the free monthly Gold book findable and the claim rate tripled — claimers renew ~18 points better, and nearly 20 in the first two months. The churn analysis says why: coming back beats reading a lot, and 3 of the first 4 weeks is worth +25 points of renewal.',
    date: '2026-08-07',
  },
  {
    slug: 'web-learnings',
    title: 'Discovery One-month Retrospect',
    subtitle: 'Five wins with the numbers behind them',
    ogDescription:
      'A month of web optimization, wins first: +123K pages into Google’s index, canonicals stopped pages competing with themselves, four route types rebuilt on one shared design system scoring 100 on accessibility and SEO, pages appearing 45% faster — and impressions back above where they started, despite giving up the [comix] term and losing the Image catalogue.',
    date: '2026-08-14',
    defaultTheme: 'dark',
  },
  {
    slug: 'marketing-resources',
    title: 'Marketing Resources',
    subtitle: 'Chapters in, trailers out — an AI-assisted marketing editor for My Studio',
    ogDescription: 'The chapter is finished, the marketing is not. Marketing Resources turns pages, characters, and translations into trailers, covers, and banners inside INKR My Studio — a VS Code-style editor, Claude Agent SDK chat, and a four-phase, demo-first plan.',
    date: '2026-08-24',
    defaultTheme: 'dark',
  },
  {
    slug: 'publisher-growth',
    title: 'Your Titles, Earning Worldwide',
    subtitle: 'The GlobalComix Growth Partnership for Publishers',
    ogDescription:
      'Localization is the entry point, not the product. A partnership that takes a publisher catalog from print files to paying readers — production, print-ready InDesign, trailers, guaranteed marketing, distribution to 2M+ readers, and a zero-upfront recoup model.',
    date: '2026-08-26',
  },
  {
    slug: 'indd-export-fixes',
    title: 'InDesign Export Fixes',
    subtitle: 'Export options, and four QA issues before and after',
    ogDescription: 'The two INDD export options side by side, then four QA issues shown before and after on the same page. Texts off position drop from 133 to 14; text verdicts passing go from 40% to 96.5%.',
    date: '2026-09-10',
    defaultTheme: 'dark',
  },
  {
    slug: 'new-app-findings',
    title: 'The New App: What We Know So Far',
    subtitle: 'People who read buy and renew the same; the gap is how many start reading',
    ogDescription:
      'Eight weeks of the new app on a random 10% of new signups. New users start reading almost twice as fast, the offer gap has closed (99% see an offer since Sep 11), and people who read buy and renew at the same rate on both apps. The gap that is left is how many new users start reading. Retention is 1.7 points lower and steady.',
    date: '2026-09-16',
  },
  {
    slug: 'seo-summer-2026',
    title: 'SEO Summer 2026',
    subtitle: 'What the search work did to the numbers, June to September',
    ogDescription:
      'Invincible ended and Image Comics left, and neither showed up in Google traffic. What moved search clicks was our own work, both ways: a title rewrite cost 25K clicks a month and was found and fixed; the index tripled; web Gold subscribers rose 157 while organic visitor-to-subscriber conversion went 0.13% to 0.18%.',
    date: '2026-09-16',
    defaultTheme: 'dark',
  },
  {
    slug: 'share-flow',
    title: 'Share Flow',
    subtitle: 'Every reader is a door to the next reader',
    ogDescription:
      'Why sharing is the growth loop for the October launch, the K-factor we steer it by, what shipped this week on the sender and friend side, and where K stands today.',
    date: '2026-10-02',
    defaultTheme: 'dark',
  },
{
    slug: 'magic-box',
    title: 'Magic Box',
    subtitle: 'How it started, how it evolved, how it expands',
    ogDescription:
      'The Magic Box is a short line from the Assistant that makes someone want to start a comic. It only works when it is personal — so we defined personal as nine facets of a reader, each from published research, and built the engine around one rule: only say what is true about the reader. Plus every place the line goes next.',
    date: '2026-10-30',
    defaultTheme: 'dark',
  },
]

export function getDeckMeta(slug: string): DeckMeta | undefined {
  return deckMetadata.find(d => d.slug === slug)
}

// URL-safe id for a companion doc, derived from its title. Stable + unique
// across a deck's docs (titles are unique). Used for /deck/:slug/docs/:docId.
export function docSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export const deckLoaders: Record<string, () => Promise<DeckModule>> = {
  'indd-export-fixes': () => import('./indd-export-fixes/init') as Promise<DeckModule>,
  'recsys-agent': () => import('./recsys-agent/init') as Promise<DeckModule>,
  'bd-agent': () => import('./bd-agent/init') as Promise<DeckModule>,
  'iris': () => import('./iris/init') as Promise<DeckModule>,
  'data-warehouse': () => import('./data-warehouse/init') as Promise<DeckModule>,
  'test-deck': () => import('./test-deck/init') as Promise<DeckModule>,
  'visitor-feed': () => import('./visitor-feed/init') as Promise<DeckModule>,
  'gem-editor': () => import('./gem-editor/init') as Promise<DeckModule>,
  'discovery-strategy': () => import('./discovery-strategy/init') as Promise<DeckModule>,
  'creator-growth': () => import('./creator-growth/init') as Promise<DeckModule>,
  'metadata-graph': () => import('./metadata-graph/init') as Promise<DeckModule>,
  'gold-retention': () => import('./gold-retention/init') as Promise<DeckModule>,
  'gold-retention-findings': () => import('./gold-retention-findings/init') as Promise<DeckModule>,
  'app-pricing-experiments': () => import('./app-pricing-experiments/init') as Promise<DeckModule>,
  'metadata-base': () => import('./metadata-base/init') as Promise<DeckModule>,
  'web-learnings': () => import('./web-learnings/init') as Promise<DeckModule>,
  'marketing-resources': () => import('./marketing-resources/init') as Promise<DeckModule>,
  'publisher-growth': () => import('./publisher-growth/init') as Promise<DeckModule>,
  'new-app-findings': () => import('./new-app-findings/init') as Promise<DeckModule>,
  'seo-summer-2026': () => import('./seo-summer-2026/init') as Promise<DeckModule>,
  'share-flow': () => import('./share-flow/init') as Promise<DeckModule>,
  'magic-box': () => import('./magic-box/init') as Promise<DeckModule>,
}

export const deckSlugs = Object.keys(deckLoaders)
